/**
 * Utility for persisting and retrieving recently viewed properties in localStorage.
 * Enables returning users to quickly pick up where they left off without losing context.
 */

const STORAGE_KEY = 'tolet_recently_viewed';
const MAX_ITEMS = 6;
export const EVENT_NAME = 'tolet_recently_viewed_changed';

export const getRecentlyViewed = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn('[recentlyViewed] Failed to parse stored items:', err);
    return [];
  }
};

export const addRecentlyViewed = (property) => {
  if (!property) return;
  const id = property._id || property.id;
  if (!id) return;

  try {
    const existing = getRecentlyViewed();
    // Exclude existing instance to prevent duplicates and move to the front
    const filtered = existing.filter((item) => (item._id || item.id) !== id);

    // Extract cleanest displayable data
    const rawImage = property.primaryImage || 
      (Array.isArray(property.images) && property.images.length > 0 ? (typeof property.images[0] === 'string' ? property.images[0] : property.images[0]?.url) : null) || 
      property.image || 
      '';

    const newItem = {
      id,
      _id: id,
      title: property.title || 'Untitled Property',
      rent: property.rent || property.dailyPrice || property.price || 0,
      city: property.city || property.address || property.location || '',
      bhk: property.bhk || (property.bedrooms ? `${property.bedrooms} BHK` : ''),
      type: property.type || 'Rental',
      image: rawImage,
      viewedAt: Date.now(),
    };

    const updated = [newItem, ...filtered].slice(0, MAX_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

    // Dispatch event so any open component on the page updates immediately
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: updated }));
    }
    return updated;
  } catch (err) {
    console.warn('[recentlyViewed] Failed to save property:', err);
    return [];
  }
};

export const removeRecentlyViewed = (id) => {
  try {
    const existing = getRecentlyViewed();
    const updated = existing.filter((item) => (item._id || item.id) !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: updated }));
    }
    return updated;
  } catch (err) {
    console.warn('[recentlyViewed] Failed to remove item:', err);
    return [];
  }
};

export const clearRecentlyViewed = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: [] }));
    }
  } catch (err) {
    console.warn('[recentlyViewed] Failed to clear items:', err);
  }
};
